// Enhanced team alerts calculation with statistical significance and actionable insights

export const calculateTeamAlerts = (surveys, teams) => {
  if (!surveys || surveys.length === 0 || !teams || teams.length === 0) {
    return [];
  }

  const alerts = [];
  const now = new Date();
  
  // Define time windows - Last 14 days vs Previous 14 days
  const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
  const twentyEightDaysAgo = new Date(now.getTime() - 28 * 24 * 60 * 60 * 1000);

  teams.forEach(team => {
    const teamSurveys = surveys.filter(s => s.teamId === team.id);
    
    if (teamSurveys.length < 10) return; // Need at least 10 surveys for meaningful analysis

    // Split surveys into recent (last 14 days) and previous (14-28 days ago)
    const recentSurveys = teamSurveys.filter(s => {
      const date = s.timestamp?.toDate ? s.timestamp.toDate() : new Date(s.timestamp);
      return date >= fourteenDaysAgo;
    });

    const previousSurveys = teamSurveys.filter(s => {
      const date = s.timestamp?.toDate ? s.timestamp.toDate() : new Date(s.timestamp);
      return date >= twentyEightDaysAgo && date < fourteenDaysAgo;
    });

    // Need minimum responses in both periods for comparison
    if (recentSurveys.length < 3 || previousSurveys.length < 3) return;

    // Calculate metrics for both periods
    const recentMetrics = calculateMetrics(recentSurveys);
    const previousMetrics = calculateMetrics(previousSurveys);

    // Analyze happiness changes
    const happinessChange = recentMetrics.happiness - previousMetrics.happiness;
    const happinessPercentChange = ((happinessChange / previousMetrics.happiness) * 100).toFixed(1);
    const happinessSignificant = Math.abs(happinessChange) >= 0.5; // 0.5 point change is significant

    // Analyze tiredness changes
    const tirednessChange = recentMetrics.tiredness - previousMetrics.tiredness;
    const tirednessPercentChange = ((tirednessChange / previousMetrics.tiredness) * 100).toFixed(1);
    const tirednessSignificant = Math.abs(tirednessChange) >= 0.5;

    // Determine alert types and priority
    const criticalLowHappiness = recentMetrics.happiness < 2.5;
    const criticalHighTiredness = recentMetrics.tiredness > 3.5;
    const warningLowHappiness = recentMetrics.happiness < 3.0 && happinessChange < 0;
    const warningHighTiredness = recentMetrics.tiredness > 3.0 && tirednessChange > 0;

    // CRITICAL ALERTS - Immediate attention needed
    if (criticalLowHappiness && happinessSignificant && happinessChange < 0) {
      alerts.push({
        teamId: team.id,
        teamName: team.name,
        type: 'critical',
        category: 'happiness',
        severity: 'high',
        title: '🚨 Critical Happiness Drop',
        message: `Team happiness has dropped to ${recentMetrics.happiness.toFixed(1)} (${happinessPercentChange}%)`,
        recentAvg: recentMetrics.happiness.toFixed(2),
        previousAvg: previousMetrics.happiness.toFixed(2),
        change: happinessChange.toFixed(2),
        percentChange: happinessPercentChange,
        recommendation: 'Schedule 1-on-1s with team members to identify issues. Consider workload review.',
        surveyCount: recentSurveys.length
      });
    }

    if (criticalHighTiredness && tirednessSignificant && tirednessChange > 0) {
      alerts.push({
        teamId: team.id,
        teamName: team.name,
        type: 'critical',
        category: 'tiredness',
        severity: 'high',
        title: '🚨 Critical Tiredness Spike',
        message: `Team tiredness has risen to ${recentMetrics.tiredness.toFixed(1)} (${tirednessPercentChange > 0 ? '+' : ''}${tirednessPercentChange}%)`,
        recentAvg: recentMetrics.tiredness.toFixed(2),
        previousAvg: previousMetrics.tiredness.toFixed(2),
        change: tirednessChange.toFixed(2),
        percentChange: tirednessPercentChange,
        recommendation: 'Review workload and deadlines. Consider redistributing tasks or extending timelines.',
        surveyCount: recentSurveys.length
      });
    }

    // WARNING ALERTS - Trending negatively
    if (warningLowHappiness && !criticalLowHappiness && happinessSignificant) {
      alerts.push({
        teamId: team.id,
        teamName: team.name,
        type: 'warning',
        category: 'happiness',
        severity: 'medium',
        title: '⚠️ Declining Happiness',
        message: `Team happiness declining: ${recentMetrics.happiness.toFixed(1)} (${happinessPercentChange}%)`,
        recentAvg: recentMetrics.happiness.toFixed(2),
        previousAvg: previousMetrics.happiness.toFixed(2),
        change: happinessChange.toFixed(2),
        percentChange: happinessPercentChange,
        recommendation: 'Monitor closely. Check in with team lead about recent challenges.',
        surveyCount: recentSurveys.length
      });
    }

    if (warningHighTiredness && !criticalHighTiredness && tirednessSignificant) {
      alerts.push({
        teamId: team.id,
        teamName: team.name,
        type: 'warning',
        category: 'tiredness',
        severity: 'medium',
        title: '⚠️ Rising Tiredness',
        message: `Team tiredness increasing: ${recentMetrics.tiredness.toFixed(1)} (${tirednessPercentChange > 0 ? '+' : ''}${tirednessPercentChange}%)`,
        recentAvg: recentMetrics.tiredness.toFixed(2),
        previousAvg: previousMetrics.tiredness.toFixed(2),
        change: tirednessChange.toFixed(2),
        percentChange: tirednessPercentChange,
        recommendation: 'Review upcoming deadlines. Consider workload adjustments.',
        surveyCount: recentSurveys.length
      });
    }

    // POSITIVE ALERTS - Celebrate improvements
    if (happinessSignificant && happinessChange > 0 && recentMetrics.happiness >= 3.5) {
      alerts.push({
        teamId: team.id,
        teamName: team.name,
        type: 'positive',
        category: 'happiness',
        severity: 'low',
        title: '🎉 Happiness Improving',
        message: `Team happiness up to ${recentMetrics.happiness.toFixed(1)} (+${happinessPercentChange}%)`,
        recentAvg: recentMetrics.happiness.toFixed(2),
        previousAvg: previousMetrics.happiness.toFixed(2),
        change: happinessChange.toFixed(2),
        percentChange: happinessPercentChange,
        recommendation: 'Great work! Share positive feedback with the team and identify what\'s working well.',
        surveyCount: recentSurveys.length
      });
    }

    if (tirednessSignificant && tirednessChange < 0 && recentMetrics.tiredness <= 2.5) {
      alerts.push({
        teamId: team.id,
        teamName: team.name,
        type: 'positive',
        category: 'tiredness',
        severity: 'low',
        title: '🎉 Tiredness Decreasing',
        message: `Team tiredness down to ${recentMetrics.tiredness.toFixed(1)} (${tirednessPercentChange}%)`,
        recentAvg: recentMetrics.tiredness.toFixed(2),
        previousAvg: previousMetrics.tiredness.toFixed(2),
        change: tirednessChange.toFixed(2),
        percentChange: tirednessPercentChange,
        recommendation: 'Team workload is well-balanced. Maintain current practices.',
        surveyCount: recentSurveys.length
      });
    }

    // COMBINED ALERT - Both metrics concerning
    if ((criticalLowHappiness || warningLowHappiness) && (criticalHighTiredness || warningHighTiredness)) {
      alerts.push({
        teamId: team.id,
        teamName: team.name,
        type: 'critical',
        category: 'combined',
        severity: 'high',
        title: '🚨 Team Burnout Risk',
        message: `Low happiness (${recentMetrics.happiness.toFixed(1)}) + High tiredness (${recentMetrics.tiredness.toFixed(1)})`,
        recentAvg: `H: ${recentMetrics.happiness.toFixed(1)} | T: ${recentMetrics.tiredness.toFixed(1)}`,
        previousAvg: `H: ${previousMetrics.happiness.toFixed(1)} | T: ${previousMetrics.tiredness.toFixed(1)}`,
        change: 'Multiple metrics',
        percentChange: 'High risk',
        recommendation: 'URGENT: Immediate team meeting required. Review workload, deadlines, and team support needs.',
        surveyCount: recentSurveys.length
      });
    }
  });

  // Sort alerts by severity (high -> medium -> low) and then by absolute change
  return alerts.sort((a, b) => {
    const severityOrder = { high: 3, medium: 2, low: 1 };
    if (severityOrder[a.severity] !== severityOrder[b.severity]) {
      return severityOrder[b.severity] - severityOrder[a.severity];
    }
    return Math.abs(parseFloat(b.change || 0)) - Math.abs(parseFloat(a.change || 0));
  });
};

// Helper function to calculate metrics for a period
const calculateMetrics = (surveys) => {
  const happiness = surveys.reduce((sum, s) => sum + (s.happiness || 0), 0) / surveys.length;
  const tiredness = surveys.reduce((sum, s) => sum + (s.adminTirednessOverall || 0), 0) / surveys.length;
  
  return {
    happiness: parseFloat(happiness),
    tiredness: parseFloat(tiredness),
    count: surveys.length
  };
};

export const sortTeams = (teams) => {
  return [...teams].sort((a, b) => {
    const aName = a.name || '';
    const bName = b.name || '';
    return aName.localeCompare(bName, undefined, { numeric: true, sensitivity: 'base' });
  });
};

export const filterEmployees = (employees, filter, search) => {
  let filtered = employees;

  if (filter !== 'all') {
    filtered = filtered.filter(emp => emp.teamId === filter);
  }

  if (search.trim()) {
    const searchLower = search.toLowerCase();
    filtered = filtered.filter(emp => 
      (emp.name && emp.name.toLowerCase().includes(searchLower)) ||
      (emp.email && emp.email.toLowerCase().includes(searchLower))
    );
  }

  return filtered.sort((a, b) => {
    const aName = a.name || a.email || '';
    const bName = b.name || b.email || '';
    return aName.localeCompare(bName);
  });
};